import type { AppAction } from "@/app/state/actions";
import type { AppState, PendingSteer } from "@/app/state/types";
import { MAX_DEBUG_LINES, MAX_EVENTS } from "@/app/state/constants";
import { bindRunAgentKey } from "@/features/chats/lib/runAgentIdentity";
import { appendVisibleDebugEvent } from "@/features/timeline/lib/debugEventDisplay";
import {
	addSetValue,
	removeSetValue,
	setMapValue,
	toggleSetValue,
} from "@/app/state/reducerHelpers";

export function reduceConversationState(
	state: AppState,
	action: AppAction,
): AppState | null {
	switch (action.type) {
		case "SET_CHAT_ID": {
			const restored = state.planningModeByChatId[action.chatId];
			const currentPlanningMode = state.planningMode;
			const isNewChatId = action.chatId !== state.chatId;
			const activeRunPlanningMode =
				restored === undefined
					? (() => {
							const chat = state.chats.find((c) => c.chatId === action.chatId);
							if (
								chat?.activeRun &&
								typeof chat.activeRun === "object" &&
								!Array.isArray(chat.activeRun)
							) {
								return Boolean(
									(chat.activeRun as Record<string, unknown>).planningMode,
								);
							}
							return false;
						})()
					: false;
			const nextPlanningMode =
				restored !== undefined
					? restored
					: activeRunPlanningMode
						? true
						: currentPlanningMode && isNewChatId
							? true
							: false;
			const nextByChatId =
				restored === undefined && currentPlanningMode && isNewChatId
					? { ...state.planningModeByChatId, [action.chatId]: true }
					: state.planningModeByChatId;

			// Save current draft for old chat
			let nextDraftByChatId = state.composerDraftByChatId;
			if (state.chatId !== action.chatId) {
				nextDraftByChatId = { ...nextDraftByChatId, [state.chatId]: state.composerDraft };
			}
			// Restore draft for new chat
			const nextComposerDraft = nextDraftByChatId[action.chatId] ?? "";

			return {
				...state,
				chatId: action.chatId,
				currentChatActiveRun: isNewChatId ? null : state.currentChatActiveRun,
				pendingNewChatAgentKey: action.chatId
					? ""
					: state.pendingNewChatAgentKey,
				planningMode: nextPlanningMode,
				planningModeByChatId: nextByChatId,
				composerDraft: nextComposerDraft,
				composerDraftByChatId: nextDraftByChatId,
			};
		}
		case "SET_CURRENT_CHAT_ACTIVE_RUN": {
			const activeRun = action.activeRun;
			if (!activeRun) {
				return { ...state, currentChatActiveRun: null };
			}
			const chatId = String(activeRun.chatId || "").trim();
			const runId = String(activeRun.runId || "").trim();
			if (!chatId || !runId) {
				return { ...state, currentChatActiveRun: null };
			}
			if (state.chatId && state.chatId !== chatId) {
				return state;
			}
			return {
				...state,
				currentChatActiveRun: {
					...activeRun,
					chatId,
					runId,
				},
			};
		}
		case "SET_RUN_ID": {
			const runId = String(action.runId || "").trim();
			return {
				...state,
				runId,
				currentRunAgentKey: runId
					? state.runAgentById.get(runId) || ""
					: "",
			};
		}
		case "SET_RUN_AGENT_BY_ID": {
			const runId = String(action.runId || "").trim();
			const agentKey = String(action.agentKey || "").trim();
			if (!runId || !agentKey) {
				return state;
			}
			const runAgentById = bindRunAgentKey(state.runAgentById, runId, agentKey);
			return {
				...state,
				runAgentById,
				currentRunAgentKey:
					state.runId === runId ? agentKey : state.currentRunAgentKey,
			};
		}
		case "SET_CURRENT_RUN_AGENT_KEY":
			return {
				...state,
				currentRunAgentKey: String(action.agentKey || "").trim(),
			};
		case "SET_REQUEST_ID":
			return { ...state, requestId: action.requestId };
		case "SET_STREAMING":
			return {
				...state,
				streaming: action.streaming,
			};
		case "SET_ABORT_CONTROLLER":
			return { ...state, abortController: action.controller };
		case "PUSH_EVENT": {
			const events =
				state.events.length >= MAX_EVENTS
					? [...state.events.slice(-Math.floor(MAX_EVENTS * 0.8)), action.event]
					: [...state.events, action.event];
			const debugEvents = appendVisibleDebugEvent(
				state.debugEvents,
				action.event,
				MAX_EVENTS,
				events,
				{
					contentNodeById: state.contentNodeById,
					reasoningNodeById: state.reasoningNodeById,
					timelineNodes: state.timelineNodes,
					activeReasoningKey: state.activeReasoningKey,
					runId: state.runId,
				},
			);
			return { ...state, events, debugEvents };
		}
		case "CLEAR_EVENTS":
			return {
				...state,
				events: [],
				debugEvents: [],
				timelineOrder: [],
				timelineNodes: new Map(),
				timelineNodeByMessageId: new Map(),
				timelineDomCache: new Map(),
				timelineCounter: 0,
				contentNodeById: new Map(),
				reasoningNodeById: new Map(),
				toolNodeById: new Map(),
				toolStates: new Map(),
				taskItemsById: new Map(),
				activeTaskIds: new Set(),
				actionStates: new Map(),
				activeReasoningKey: "",
				activeFrontendTool: null,
				activeAwaiting: null,
				pendingAwaitings: [],
			};
		case "APPEND_DEBUG": {
			const debugLines =
				state.debugLines.length >= MAX_DEBUG_LINES
					? [
							...state.debugLines.slice(-Math.floor(MAX_DEBUG_LINES * 0.8)),
							action.line,
						]
					: [...state.debugLines, action.line];
			return { ...state, debugLines };
		}
		case "CLEAR_DEBUG":
			return { ...state, debugLines: [] };
		case "SET_MESSAGE":
			return {
				...state,
				messagesById: setMapValue(state.messagesById, action.id, action.message),
			};
		case "SET_MESSAGE_ORDER":
			return { ...state, messageOrder: action.order };
		case "SET_COMPOSER_DRAFT": {
			const { chatId, composerDraftByChatId } = state;
			return {
				...state,
				composerDraft: action.draft,
				composerDraftByChatId: { ...composerDraftByChatId, [chatId]: action.draft },
			};
		}
		case "ENQUEUE_PENDING_STEER": {
			const chatId = state.chatId || "";
			const existing = state.pendingSteers[chatId] || [];
			return {
				...state,
				pendingSteers: { ...state.pendingSteers, [chatId]: [...existing, action.steer] },
			};
		}
		case "UPDATE_PENDING_STEER_STATUS": {
			const next: Record<string, PendingSteer[]> = {};
			let changed = false;
			for (const cid of Object.keys(state.pendingSteers)) {
				const updated = state.pendingSteers[cid].map((steer) =>
					steer.steerId === action.steerId
						? ((changed = true), { ...steer, status: action.status })
						: steer,
				);
				next[cid] = changed ? updated : state.pendingSteers[cid];
			}
			if (!changed) return state;
			return { ...state, pendingSteers: next };
		}
		case "REMOVE_PENDING_STEER": {
			const next: Record<string, PendingSteer[]> = {};
			let removed = false;
			for (const cid of Object.keys(state.pendingSteers)) {
				const filtered = state.pendingSteers[cid].filter(
					(steer) => steer.steerId !== action.steerId,
				);
				if (filtered.length < state.pendingSteers[cid].length) {
					removed = true;
				}
				if (filtered.length === 0) continue;
				next[cid] = filtered;
			}
			if (!removed) return state;
			return { ...state, pendingSteers: next };
		}
		case "CLEAR_PENDING_STEERS": {
			const chatId = state.chatId || "";
			const existing = state.pendingSteers[chatId];
			if (!existing || existing.length === 0) {
				return state;
			}
			return { ...state, pendingSteers: { ...state.pendingSteers, [chatId]: [] } };
		}
		case "TOGGLE_RUN_DOWNVOTE":
			return {
				...state,
				downvotedRunKeys: toggleSetValue(state.downvotedRunKeys, action.runKey),
			};
		case "SET_RUN_DOWNVOTED":
			return {
				...state,
				downvotedRunKeys: action.downvoted
					? addSetValue(state.downvotedRunKeys, action.runKey)
					: removeSetValue(state.downvotedRunKeys, action.runKey),
			};
		case "ADD_EXECUTED_ACTION_ID":
			return {
				...state,
				executedActionIds: addSetValue(state.executedActionIds, action.actionId),
			};
		default:
			return null;
	}
}
